import { useState, type CSSProperties } from 'react';

export interface LoaderSpinnerProps {
  /**
   * The smaller box the section loader uses. Only the box changes — the mark is
   * sized as a percentage of it, so one flag moves both parts.
   */
  small?: boolean;
}

/**
 * The turning part of every loader: the Xtremand mark, with a ring going round it.
 *
 * <h2>Why this is a component and not eight lines repeated twice</h2>
 *
 * There are three places this has to look identical: the static markup in
 * `index.html` that paints before React exists, {@link AppLoader}, and
 * {@link SectionLoader}. The first cannot be a component — it renders before
 * there is a React — so it stays hand-written and `AppLoader.test.tsx` asserts it
 * agrees with this file.
 *
 * <p>The other two can be, and are. A bare `<div className="xa-loader__spinner" />`
 * was the same in both while the spinner was a CSS border and nothing else; with
 * an `<img>` inside it there is a source path, an `alt` and two dimensions to keep
 * in step, and every one of those is a way for the two to drift. That drift is the
 * whole history this loader was rebuilt to end.
 *
 * <h2>Where the styling is, and why the mark comes from `public/`</h2>
 *
 * In the `<style>` block in `index.html`, with the rest of the loader —
 * `.xa-loader__spinner`, its `::before` ring, its `::after` disc, and
 * `.xa-loader__logo`. The reasons are in that file: it must apply before any
 * stylesheet arrives, and one definition cannot drift from itself.
 *
 * <p>The mark is `/xtremand-mark.png`, served from `public/`, for the matching
 * reason. A `src/assets/` import resolves to a hashed URL that only exists after
 * the bundler has run, so the static copy in `index.html` could not name the same
 * file; a `public/` path is the identical string in development and in a build.
 * It is 4.5 kB — generated from the 512px master in `src/assets/Xtremand-icon.png`,
 * cropped to the artwork and re-padded square so it sits centred in the ring.
 */
export function LoaderSpinner({ small = false }: LoaderSpinnerProps) {
  /*
   * WHERE THE RING HAD GOT TO WHEN THIS REPLACED THE STATIC ONE.
   *
   * A CSS animation starts when its element is created, and React's first commit does not
   * adopt `index.html`'s loader -- it destroys it and builds this one. So without this the
   * ring restarts at 0deg around 700ms in, having been most of the way round a moment
   * earlier, and the arc visibly jumps backwards. That is a flicker no amount of
   * compositing can remove, because nothing is being dropped: it is the animation
   * genuinely starting again.
   *
   * `performance.now()` is milliseconds since the document began, which is within a frame
   * of when the static copy was parsed. Handing it back as a NEGATIVE delay starts this
   * animation that far in, so it picks up exactly where its predecessor was.
   *
   * Captured in a state initialiser rather than read during render: a re-render must not
   * re-phase a ring that is already turning correctly. Held as a STRING because
   * `restrict-template-expressions` refuses a number in a template literal, and rounding it
   * here is also the only place it needs rounding.
   */
  const [phase] = useState(() => performance.now().toFixed(0));

  return (
    <div
      // A custom property, so the value reaches ::before -- which is where the animation
      // is, and which cannot take a style attribute of its own. The cast is React's
      // CSSProperties not admitting custom properties; it asserts a type, it does not
      // widen one, and there is no `any` here.
      style={{ '--xa-spin-phase': `-${phase}ms` } as CSSProperties}
      // Hidden from assistive technology. Announced, this would be an
      // unlabelled graphic inside a live region that is about to speak anyway --
      // AppLoader's clipped word, or SectionLoader's visible label.
      aria-hidden
      className={small ? 'xa-loader__spinner xa-loader__spinner--sm' : 'xa-loader__spinner'}
    >
      {/*
        The mark, and nothing else. The ring and the disc that makes it a ring are
        both pseudo-elements of this box in index.html, so there is no markup for
        them here and nothing that can drift from the static copy.

        It was an inline <svg> with a linearGradient until 30 September 2026, whose
        id had to be kept identical to index.html's for the handover. There is no
        id now, because there is no gradient element -- the sweep is a
        conic-gradient in CSS, which is what xtremand.com itself draws.

        The <img> is the same element index.html carries, attribute for attribute.
        `alt=""` because the parent is aria-hidden and the announcement comes from
        the clipped word beside it.
      */}
      <img
        className="xa-loader__logo"
        src="/xtremand-mark.png"
        alt=""
        width={128}
        height={128}
        fetchPriority="high"
      />
    </div>
  );
}
